import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../lib/api";

type ToastKind = "info" | "success" | "warning" | "error";
export interface Toast { id: number; kind: ToastKind; message: string }

interface ToastApi {
  toasts: Toast[];
  toast: (message: string, kind?: ToastKind) => void;
  /** Shows an API error translated via `errors.<CODE>` (falls back to the server message). */
  toastError: (err: unknown) => void;
  dismiss: (id: number) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => setToasts((l) => l.filter((x) => x.id !== id)), []);
  const toast = useCallback(
    (message: string, kind: ToastKind = "info") => {
      const id = ++seq.current;
      setToasts((l) => [...l.slice(-4), { id, kind, message }]);
      setTimeout(() => dismiss(id), kind === "error" ? 6000 : 3500);
    },
    [dismiss],
  );
  const toastError = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError) {
        const key = `errors.${err.code}`;
        // Translated by error code (details fill placeholders like {{max}}); fall back to the server text.
        const msg = i18n.exists(key) ? String(t(key, (err.details ?? {}) as Record<string, unknown>)) : "";
        toast(msg || err.message || String(t("errors.UNKNOWN")), "error");
      } else toast(String(t("errors.UNKNOWN")), "error");
    },
    [t, i18n, toast],
  );

  const value = useMemo(() => ({ toasts, toast, toastError, dismiss }), [toasts, toast, toastError, dismiss]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast must be used inside <ToastProvider>");
  return v;
}
